import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import { twoFactorService } from '../../services/twoFactorService';
import { subscriptionService } from '../../services/subscriptionService';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const Profile = () => {
  const { user, userProfile, profileLoading } = useAuth();
  const { subscription, hasActiveSubscription, loading: subscriptionLoading, refreshSubscription } = useSubscription();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [fullName, setFullName] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // 2FA state
  const [twoFactorStatus, setTwoFactorStatus] = useState(null);
  const [twoFactorLoading, setTwoFactorLoading] = useState(true);

  // Subscription state
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  // Notification state
  const [pushEnabled, setPushEnabled] = useState(false);
  const [emailPreferences, setEmailPreferences] = useState({
    paymentConfirmations: true,
    failedTransactions: true,
    subscriptionRenewals: true,
    billingAlerts: true
  });

  useEffect(() => {
    if (userProfile) {
      setFullName(userProfile?.full_name || '');
      setWalletAddress(userProfile?.wallet_address || '');
    }
  }, [userProfile]);

  useEffect(() => {
    load2FAStatus();
  }, []);

  useEffect(() => {
    if (user?.id) {
      loadTransactions();
    }
  }, [user?.id]);

  // Check for upgrade query param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params?.get('upgrade') === 'true' && userProfile?.tier === 'free') {
      // Scroll to subscription section
      setTimeout(() => {
        document.getElementById('subscription-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, [userProfile?.tier]);

  const load2FAStatus = async () => {
    setTwoFactorLoading(true);
    try {
      const status = await twoFactorService?.get2FAStatus();
      setTwoFactorStatus(status);
    } catch (err) {
      console.error('Failed to load 2FA status:', err);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const loadTransactions = async () => {
    setTransactionsLoading(true);
    try {
      const { data } = await subscriptionService?.getPaymentTransactions(user?.id);
      setTransactions(data || []);
    } catch (err) {
      console.error('Failed to load transactions:', err);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const handleUpgradeToPro = async () => {
    setUpgrading(true);
    setError('');

    try {
      const { data, error: checkoutError } = await subscriptionService?.createCheckoutSession(user?.id, 'pro');
      
      if (checkoutError) {
        setError(checkoutError?.message);
        return;
      }

      // Redirect to Stripe Checkout
      if (data?.url) {
        window.location.href = data?.url;
      } else {
        setError('Failed to create checkout session');
      }
    } catch (err) {
      setError(err?.message || 'Failed to start upgrade process');
    } finally {
      setUpgrading(false);
    }
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    setError('');

    try {
      const { data, error: portalError } = await subscriptionService?.createPortalSession(
        subscription?.stripeCustomerId || userProfile?.stripe_customer_id
      );
      
      if (portalError) {
        setError(portalError?.message);
        return;
      }

      // Redirect to Stripe Customer Portal
      if (data?.url) {
        window.location.href = data?.url;
      } else {
        setError('Failed to open subscription management');
      }
    } catch (err) {
      setError(err?.message || 'Failed to open subscription management');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const { error } = await authService?.updateProfile({
        fullName,
        walletAddress: walletAddress || null
      });

      if (error) {
        setError(error?.message);
      } else {
        setSuccess('Profile updated successfully');
        setEditing(false);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectWallet = async () => {
    if (!walletAddress) {
      setError('Please enter a wallet address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error } = await authService?.connectWallet(walletAddress);
      if (error) {
        setError(error?.message);
      } else {
        setSuccess('Wallet connected successfully');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err?.message || 'Failed to connect wallet');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectWallet = async () => {
    setLoading(true);
    setError('');

    try {
      const { error } = await authService?.disconnectWallet();
      if (error) {
        setError(error?.message);
      } else {
        setWalletAddress('');
        setSuccess('Wallet disconnected successfully');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err?.message || 'Failed to disconnect wallet');
    } finally {
      setLoading(false);
    }
  };

  const handleEnablePush = async () => {
    try {
      // Request browser notification permission
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setPushEnabled(true);
        setSuccess('Push notifications enabled');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Push notification permission denied');
        setTimeout(() => setError(''), 3000);
      }
    } catch (err) {
      console.error('Failed to enable push notifications:', err);
      setError('Failed to enable push notifications');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleEmailPreferenceChange = (preference) => {
    setEmailPreferences(prev => ({
      ...prev,
      [preference]: !prev?.[preference]
    }));
    setSuccess('Email preferences updated');
    setTimeout(() => setSuccess(''), 3000);
  };

  const notificationService = {
    getPushPermissionStatus: () => {
      if (typeof Notification === 'undefined') {
        return 'unsupported';
      }
      return Notification.permission;
    }
  };

  const getTierBadge = () => {
    const tier = userProfile?.tier;
    if (tier === 'pro') {
      return (
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/20 text-primary rounded-full">
          <Icon name="Crown" size={16} />
          <span className="text-sm font-semibold">Pro Tier</span>
          {hasActiveSubscription && (
            <span className="text-xs opacity-75">• Active</span>
          )}
        </div>
      );
    }
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1 bg-muted text-muted-foreground rounded-full">
        <Icon name="User" size={16} />
        <span className="text-sm font-semibold">Free Tier</span>
      </div>
    );
  };

  const getWalletStatusBadge = () => {
    const status = userProfile?.wallet_status;
    if (status === 'connected') {
      return (
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-success/20 text-success rounded-full">
          <Icon name="CheckCircle" size={16} />
          <span className="text-sm font-semibold">Connected</span>
        </div>
      );
    }
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1 bg-muted text-muted-foreground rounded-full">
        <Icon name="XCircle" size={16} />
        <span className="text-sm font-semibold">Disconnected</span>
      </div>
    );
  };

  if (profileLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Icon name="Loader" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading profile...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Profile Settings</h1>
          <p className="text-muted-foreground">Manage your account information and security settings</p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 bg-success/10 border border-success rounded-lg p-4 flex items-start gap-3">
            <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
            <p className="text-sm text-success">{success}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-error/10 border border-error rounded-lg p-4 flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
            <p className="text-sm text-error">{error}</p>
          </div>
        )}

        <div className="space-y-6">
          {/* Account Information Card */}
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center">
                <Icon name="User" size={40} className="text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  {userProfile?.full_name || 'User'}
                </h2>
                <p className="text-muted-foreground mb-3">{user?.email}</p>
                <div className="flex flex-wrap gap-2">
                  {getTierBadge()}
                  {getWalletStatusBadge()}
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-6 space-y-4">
              <h3 className="text-lg font-semibold text-foreground">Account Information</h3>
              
              {editing ? (
                <div className="space-y-4">
                  <Input
                    label="Full Name"
                    value={fullName}
                    onChange={(e) => setFullName(e?.target?.value)}
                    placeholder="Enter your full name"
                  />
                  <Input
                    label="Email"
                    value={user?.email}
                    disabled
                    description="Email cannot be changed"
                  />
                  <div className="flex gap-3">
                    <Button
                      onClick={handleUpdate}
                      loading={loading}
                      iconName="Save"
                    >
                      Save Changes
                    </Button>
                    <Button
                      onClick={() => {
                        setEditing(false);
                        setFullName(userProfile?.full_name || '');
                        setError('');
                      }}
                      variant="outline"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Full Name</label>
                    <p className="text-foreground">{userProfile?.full_name || 'Not set'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    <p className="text-foreground">{user?.email}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Two-Factor Authentication Card */}
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Icon name="Shield" size={20} className="text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">Two-Factor Authentication</h2>
                  <p className="text-sm text-muted-foreground">Add an extra layer of security to your account</p>
                </div>
              </div>
            </div>

            {twoFactorLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Status Overview */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-foreground">Status</span>
                    {twoFactorStatus?.has2fa ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                        <Icon name="CheckCircle" size={14} />
                        Enabled
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                        <Icon name="AlertCircle" size={14} />
                        Not Enabled
                      </span>
                    )}
                  </div>

                  {twoFactorStatus?.has2fa && (
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Active Methods</p>
                        <p className="text-lg font-semibold text-foreground">{twoFactorStatus?.enabledMethods || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Primary Method</p>
                        <p className="text-sm font-medium text-foreground capitalize">
                          {twoFactorStatus?.primaryMethod?.replace('_', ' ') || 'None'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Backup Codes</p>
                        <p className="text-lg font-semibold text-foreground">{twoFactorStatus?.backupCodesRemaining || 0}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  {!twoFactorStatus?.has2fa ? (
                    <Button
                      onClick={() => navigate('/two-factor-authentication-setup')}
                      iconName="Shield"
                      variant="default"
                    >
                      Enable Two-Factor Authentication
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={() => navigate('/two-factor-authentication-management')}
                        iconName="Settings"
                        variant="outline"
                      >
                        Manage 2FA Settings
                      </Button>
                      <Button
                        onClick={() => navigate('/two-factor-authentication-setup')}
                        iconName="Plus"
                        variant="outline"
                      >
                        Add Method
                      </Button>
                    </>
                  )}
                </div>

                {/* Security Information */}
                {!twoFactorStatus?.has2fa && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                    <div className="flex items-start gap-3">
                      <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-blue-900 mb-1">Why enable 2FA?</p>
                        <ul className="text-sm text-blue-800 space-y-1">
                          <li>• Protects your account even if your password is compromised</li>
                          <li>• Prevents unauthorized access to your wallet and funds</li>
                          <li>• Industry-standard security practice for financial platforms</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Wallet Connection Card */}
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="border-t border-border pt-6 space-y-4">
              <h3 className="text-lg font-semibold text-foreground">Wallet Connection</h3>
              {userProfile?.wallet_status === 'connected' ? (
                <div className="space-y-3">
                  <div className="bg-success/10 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground mb-1">Connected Wallet</p>
                    <p className="text-foreground font-mono text-sm break-all">
                      {userProfile?.wallet_address}
                    </p>
                  </div>
                  <Button
                    onClick={handleDisconnectWallet}
                    loading={loading}
                    variant="destructive"
                    iconName="Unlink"
                  >
                    Disconnect Wallet
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Input
                    label="Wallet Address"
                    value={walletAddress}
                    onChange={(e) => setWalletAddress(e?.target?.value)}
                    placeholder="0x..."
                    description="Enter your crypto wallet address"
                  />
                  <Button
                    onClick={handleConnectWallet}
                    loading={loading}
                    iconName="Link"
                  >
                    Connect Wallet
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Account Tier Card */}
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="border-t border-border pt-6 space-y-4">
              <h3 className="text-lg font-semibold text-foreground">Tier Limits</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-1">Spending Limit</p>
                  <p className="text-2xl font-bold text-foreground">
                    ${userProfile?.spending_limit?.toLocaleString() || '0'}
                  </p>
                </div>
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-1">Monthly Swaps</p>
                  <p className="text-2xl font-bold text-foreground">
                    {userProfile?.monthly_swap_limit || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Subscription Management Card */}
          <div id="subscription-section" className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-1">Subscription</h3>
                <p className="text-sm text-muted-foreground">Manage your subscription and billing</p>
              </div>
              {getTierBadge()}
            </div>

            {subscriptionLoading ? (
              <div className="flex items-center justify-center py-8">
                <Icon name="Loader" size={24} className="animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Current Plan */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="font-semibold text-foreground mb-1">
                        {userProfile?.tier === 'pro' ? 'Pro Plan' : 'Free Plan'}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {userProfile?.tier === 'pro' 
                          ? `$${subscriptionService?.formatAmount(29.99, 'USD')}/month`
                          : 'No subscription required'
                        }
                      </p>
                    </div>
                    {userProfile?.tier === 'pro' && subscription && (
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground mb-1">Next billing date</p>
                        <p className="text-sm font-medium text-foreground">
                          {new Date(subscription?.currentPeriodEnd)?.toLocaleDateString()}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Plan Features */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Check" size={16} className="text-success" />
                      <span className="text-foreground">
                        ${userProfile?.spending_limit?.toLocaleString()} spending limit
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Check" size={16} className="text-success" />
                      <span className="text-foreground">
                        {userProfile?.monthly_swap_limit} swaps per month
                      </span>
                    </div>
                    {userProfile?.tier === 'pro' && (
                      <>
                        <div className="flex items-center gap-2 text-sm">
                          <Icon name="Check" size={16} className="text-success" />
                          <span className="text-foreground">Advanced analytics</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Icon name="Check" size={16} className="text-success" />
                          <span className="text-foreground">Priority support</span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Action Buttons */}
                  {userProfile?.tier === 'free' ? (
                    <Button
                      onClick={handleUpgradeToPro}
                      loading={upgrading}
                      iconName="Crown"
                      className="w-full"
                    >
                      Upgrade to Pro - $29.99/month
                    </Button>
                  ) : (
                    <Button
                      onClick={handleManageSubscription}
                      loading={loading}
                      variant="outline"
                      iconName="Settings"
                      className="w-full"
                    >
                      Manage Subscription
                    </Button>
                  )}
                </div>

                {/* Billing History */}
                {userProfile?.tier === 'pro' && transactions?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-foreground mb-3">Billing History</h4>
                    <div className="space-y-2">
                      {transactions?.slice(0, 5)?.map((tx) => (
                        <div
                          key={tx?.id}
                          className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                              <Icon name="CreditCard" size={16} className="text-primary" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground">
                                {tx?.description || 'Payment'}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(tx?.createdAt)?.toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-foreground">
                              {subscriptionService?.formatAmount(tx?.amount, tx?.currency)}
                            </p>
                            <p className="text-xs text-success capitalize">{tx?.status}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Subscription Status Warning */}
                {userProfile?.tier === 'pro' && subscription?.cancelAtPeriodEnd && (
                  <div className="bg-warning/10 border border-warning rounded-lg p-4 flex items-start gap-3">
                    <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-warning mb-1">
                        Subscription Ending
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Your subscription will end on {new Date(subscription?.currentPeriodEnd)?.toLocaleDateString()}. 
                        You'll be downgraded to the Free tier.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Notification Preferences Card */}
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <Icon name="Bell" size={20} className="text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">Notification Preferences</h2>
                <p className="text-sm text-muted-foreground">Manage how you receive alerts</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Browser Push Notifications */}
              {notificationService?.getPushPermissionStatus() !== 'unsupported' && (
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-3">Browser Notifications</h3>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Icon name="Monitor" size={20} className="text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium text-foreground">Real-time Push Notifications</p>
                          <p className="text-xs text-muted-foreground">Get instant alerts in your browser</p>
                        </div>
                      </div>
                      {pushEnabled ? (
                        <div className="flex items-center gap-2">
                          <Icon name="CheckCircle" size={16} className="text-success" />
                          <span className="text-sm text-success font-medium">Enabled</span>
                        </div>
                      ) : (
                        <Button
                          onClick={handleEnablePush}
                          size="sm"
                          variant="outline"
                        >
                          Enable
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Email Notifications */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3">Email Notifications</h3>
                <div className="space-y-3">
                  <div className="bg-muted/30 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon name="CheckCircle" size={20} className="text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Payment Confirmations</p>
                        <p className="text-xs text-muted-foreground">Receive emails for successful payments</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleEmailPreferenceChange('paymentConfirmations')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        emailPreferences?.paymentConfirmations ? 'bg-primary' : 'bg-muted'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          emailPreferences?.paymentConfirmations ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="bg-muted/30 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon name="AlertCircle" size={20} className="text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Failed Transactions</p>
                        <p className="text-xs text-muted-foreground">Get notified when payments fail</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleEmailPreferenceChange('failedTransactions')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        emailPreferences?.failedTransactions ? 'bg-primary' : 'bg-muted'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          emailPreferences?.failedTransactions ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="bg-muted/30 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon name="RefreshCw" size={20} className="text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Subscription Renewals</p>
                        <p className="text-xs text-muted-foreground">Reminders before subscription renews</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleEmailPreferenceChange('subscriptionRenewals')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        emailPreferences?.subscriptionRenewals ? 'bg-primary' : 'bg-muted'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          emailPreferences?.subscriptionRenewals ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="bg-muted/30 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon name="AlertTriangle" size={20} className="text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Billing Alerts</p>
                        <p className="text-xs text-muted-foreground">Important billing notifications</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleEmailPreferenceChange('billingAlerts')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        emailPreferences?.billingAlerts ? 'bg-primary' : 'bg-muted'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          emailPreferences?.billingAlerts ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-start gap-3">
                <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-foreground mb-1">
                    <strong>Email notifications are sent via Resend</strong>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    You'll receive emails for payment confirmations, failed transactions, upcoming renewals, and billing alerts based on your preferences above.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Profile;